import { useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import * as Sentry from '@sentry/react'
import Modal from './Modal'
import { apiFetch } from '../services/api'

interface Props {
  session: Session
  onClose: () => void
}

type Category = 'payment' | 'bug' | 'feature' | 'other'

const CATEGORIES: { value: Category; label: string }[] = [
  { value: 'payment', label: 'Problema con pagamento' },
  { value: 'bug', label: 'Problema con il sito' },
  { value: 'feature', label: 'Suggerisci una funzionalità' },
  { value: 'other', label: 'Altro' },
]

export default function SupportModal({ session, onClose }: Props) {
  const [category, setCategory] = useState<Category>('bug')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const body: Record<string, unknown> = { category, message }
    if (category === 'bug') {
      body.user_agent = navigator.userAgent
      body.current_url = window.location.href
    }

    try {
      await apiFetch<void>('/support', session, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      if (category === 'bug') {
        Sentry.captureMessage('User-reported bug', {
          level: 'warning',
          tags: { source: 'support_form' },
          extra: { message, current_url: window.location.href },
        })
      }
      setSent(true)
    } catch (err: unknown) {
      const e = err as Error & { status?: number }
      setError(e.message ?? 'Invio fallito. Riprova.')
    } finally {
      setLoading(false)
    }
  }

  if (sent) {
    return (
      <Modal title="Supporto" onClose={onClose}>
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="font-semibold text-gray-800">Messaggio inviato</p>
          <p className="text-sm text-gray-500">Ti risponderemo il prima possibile.</p>
          <button
            onClick={onClose}
            className="mt-2 text-sm font-medium bg-sky-600 hover:bg-sky-700 text-white px-5 py-2 rounded-lg transition-colors"
          >
            Chiudi
          </button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal title="Contattaci" onClose={onClose} formId="support-form">
      <form id="support-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Categoria</label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value as Category)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
          >
            {CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Messaggio</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            required
            minLength={10}
            maxLength={2000}
            rows={5}
            placeholder="Descrivi il problema o la tua richiesta..."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
          />
          <p className="text-xs text-gray-400 mt-1 text-right">{message.length}/2000</p>
        </div>

        {category === 'bug' && (
          <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
            Le informazioni tecniche (browser, URL) verranno allegate automaticamente per aiutarci a risolvere il problema.
          </p>
        )}

        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
        )}

        {loading && (
          <p className="text-sm text-gray-500 text-center">Invio in corso...</p>
        )}
      </form>
    </Modal>
  )
}
